// bd-deepcontext-b4zw — the create/update/toggle/execute/delete calls from
// ScheduledJobs.tsx. Each resolves to `true` only when the call succeeded, so the
// page knows whether to close its form; banners and the shared spinner are
// driven through the setters the page owns.
import { useCallback } from "react";
import type { BlueprintUsageMap } from "../PromptMultiSelect";
import type { ScheduledJobFormData } from "../scheduledJobFormDefaults";
import { scheduledJobsApi } from "./api";
import { extractApiErrorMessage, formatBlueprintConflict } from "./apiErrors";
import { buildCreatePayload, buildUpdatePayload } from "./formPayload";
import type { ScheduledJob } from "./types";

export interface DuplicateJobOptions {
  duplicateOfJobId: string;
  duplicateSourceName: string;
}

export interface JobMutations {
  createJob: (
    formData: ScheduledJobFormData,
    duplicateOptions?: DuplicateJobOptions,
  ) => Promise<boolean>;
  updateJob: (jobId: string, formData: ScheduledJobFormData) => Promise<boolean>;
  toggleActive: (job: ScheduledJob) => Promise<void>;
  executeJob: (job: ScheduledJob) => Promise<void>;
  deleteJob: (job: ScheduledJob) => Promise<void>;
}

export function useJobMutations({
  blueprintUsage,
  includeLocalModel = false,
  reload,
  setLoading,
  setError,
  setSuccess,
}: {
  blueprintUsage: BlueprintUsageMap;
  /** deepcontext-7px2.4 — isLocalModelAllowed(user, impersonation). */
  includeLocalModel?: boolean;
  reload: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (message: string | null) => void;
  setSuccess: (message: string | null) => void;
}): JobMutations {
  const createJob = useCallback(
    async (
      formData: ScheduledJobFormData,
      duplicateOptions?: DuplicateJobOptions,
    ) => {
      try {
        setLoading(true);
        setError(null);

        await scheduledJobsApi.createJob(
          buildCreatePayload(
            formData,
            duplicateOptions?.duplicateOfJobId,
            includeLocalModel,
          ),
        );

        setSuccess(
          duplicateOptions
            ? `Job duplicated; ${duplicateOptions.duplicateSourceName} deactivated`
            : "Job created successfully",
        );
        reload();
        return true;
      } catch (err: any) {
        console.error("Failed to create job:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to create scheduled jobs");
        } else if (err.response?.status === 409) {
          // bd-n3yqb backstop — a blueprint was claimed by another job (race the
          // disabling couldn't catch). Name the blueprint(s) + owning job(s).
          setError(formatBlueprintConflict(err, blueprintUsage));
        } else {
          // Show the actual error message from the backend
          setError(extractApiErrorMessage(err, "Failed to create job"));
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [blueprintUsage, includeLocalModel, reload, setError, setLoading, setSuccess],
  );

  const updateJob = useCallback(
    async (jobId: string, formData: ScheduledJobFormData) => {
      try {
        setLoading(true);
        setError(null);

        await scheduledJobsApi.updateJob(
          jobId,
          buildUpdatePayload(formData, includeLocalModel),
        );

        setSuccess("Job updated successfully");
        reload();
        return true;
      } catch (err: any) {
        console.error("Failed to update job:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to update scheduled jobs");
        } else if (err.response?.status === 409) {
          // bd-n3yqb backstop — blueprint claimed by another job mid-edit.
          setError(formatBlueprintConflict(err, blueprintUsage));
        } else {
          setError(extractApiErrorMessage(err, "Failed to update job"));
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [blueprintUsage, includeLocalModel, reload, setError, setLoading, setSuccess],
  );

  const toggleActive = useCallback(
    async (job: ScheduledJob) => {
      try {
        setLoading(true);
        setError(null);

        await scheduledJobsApi.updateJob(job.id, { isActive: !job.isActive });

        setSuccess(
          `Job ${job.isActive ? "deactivated" : "activated"} successfully`,
        );
        reload();
      } catch (err: any) {
        console.error("Failed to toggle job status:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to modify scheduled jobs");
        } else {
          setError(extractApiErrorMessage(err, "Failed to toggle job status"));
        }
      } finally {
        setLoading(false);
      }
    },
    [reload, setError, setLoading, setSuccess],
  );

  const executeJob = useCallback(
    async (job: ScheduledJob) => {
      try {
        setLoading(true);
        setError(null);

        await scheduledJobsApi.executeJob(job.id);
        setSuccess("Job executed successfully");
        reload();
      } catch (err: any) {
        console.error("Failed to execute job:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to execute scheduled jobs");
        } else {
          setError(extractApiErrorMessage(err, "Failed to execute job"));
        }
      } finally {
        setLoading(false);
      }
    },
    [reload, setError, setLoading, setSuccess],
  );

  const deleteJob = useCallback(
    async (job: ScheduledJob) => {
      if (!window.confirm(`Are you sure you want to delete "${job.name}"?`)) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        await scheduledJobsApi.deleteJob(job.id);
        setSuccess("Job deleted successfully");
        reload();
      } catch (err: any) {
        console.error("Failed to delete job:", err);
        if (err.response?.status === 403) {
          setError("Admin access required to delete scheduled jobs");
        } else {
          setError(extractApiErrorMessage(err, "Failed to delete job"));
        }
      } finally {
        setLoading(false);
      }
    },
    [reload, setError, setLoading, setSuccess],
  );

  return { createJob, updateJob, toggleActive, executeJob, deleteJob };
}
